import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";
import { jsonToQuery } from "@/utils/queryHelper";

/**
 * Ferry Route Service
 * Rota bazlı feribot API'leri için servis
 */
export const ferryRouteService = {
  /**
   * Rotanın en ucuz seferlerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.Origin - Kalkış noktası (örn: "istanbul")
   * @param {string} payload.Destination - Varış noktası (örn: "ankara")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "Origin": "istanbul",
   *   "Destination": "ankara",
   *   "CurrencyCode": "TRY"
   * }
   */
  getCheapestTrips: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.ferry.route.getCheapestTrips,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Rota bilgilerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.Origin - Kalkış noktası (örn: "istanbul")
   * @param {string} payload.Destination - Varış noktası (örn: "ankara")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "Origin": "istanbul",
   *   "Destination": "ankara",
   *   "CurrencyCode": "TRY"
   * }
   */
  getRouteInfo: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.ferry.route.getRouteInfo,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Rotanın popüler firmalarını getir
   * @param {Object} query - API query
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.Origin - Kalkış noktası (örn: "istanbul")
   * @param {string} payload.Destination - Varış noktası (örn: "ankara")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "Origin": "istanbul",
   *   "Destination": "ankara"
   * }
   */
  getPopularCompanies: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getPopularCompanies}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Rota hakkında bilgileri getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.Origin - Kalkış noktası (örn: "istanbul")
   * @param {string} payload.Destination - Varış noktası (örn: "ankara")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "Origin": "istanbul",
   *   "Destination": "ankara",
   *   "CurrencyCode": "TRY"
   * }
   */
  getRouteAbout: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.ferry.route.getRouteAbout,
      payload,
      headers,
      hasErrorHandler,
    }),

  getCheapestFerriesBetween: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestFerriesBetween}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getCheapestFerriesFromOrigin: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestFerriesFromOrigin}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getCheapestFerriesFromOriginForTicketCards: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestFerriesFromOriginForTicketCards}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getCheapestFerriesFromPort: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestFerriesFromPort}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getCheapestFerriesFromPortForTicketCards: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestFerriesFromPortForTicketCards}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),
  getCheapestFerriesToPort: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestFerriesToPort}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getPortInfo: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getPortInfo}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getClosestPorts: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getClosestPorts}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getPopularCompaniesByPort: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getPopularCompaniesByPort}?${jsonToQuery(
        query,
      )}`,
      headers,
      hasErrorHandler,
    }),

  getPortQAContent: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getPortQAContent}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getCheapestFerriesToDestination: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestFerriesToDestination}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Rota seyahat bilgilerini getir
   * @param {Object} query - API query
   * @param {string} query.languageCode - Dil kodu (örn: "tr", "en")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {string} query.originId - Kalkış noktası (örn: "1")
   * @param {string} query.destinationId - Varış noktası (örn: "2")
   * @param {Object} headers - HTTP headers
   * **/
  getRouteTravelHighlights: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getRouteTravelHighlights}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  getDestinationTravelHighlights: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getDestinationTravelHighlights}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Firma için en ucuz feribot seferlerini getir
   * @param {Object} query - API query
   * @param {string} query.companyId - Firma ID
   * @param {string} query.languageCode - Dil kodu (örn: "tr", "en")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getCheapestFerriesForCompany: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestFerriesForCompany}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Rota içeriğini getir (expandable text için)
   * @param {Object} query - API query
   * @param {string} query.originId - Kalkış noktası ID
   * @param {string} query.destinationId - Varış noktası ID
   * @param {string} query.languageCode - Dil kodu (örn: "tr", "en")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getRouteContent: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getRouteContent}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Rota QA içeriğini getir (FAQ için)
   * @param {Object} query - API query
   * @param {string} query.originId - Kalkış noktası ID
   * @param {string} query.destinationId - Varış noktası ID
   * @param {string} query.languageCode - Dil kodu (örn: "tr", "en")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getRouteQAContent: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getRouteQAContent}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Firma için rotaları getir
   * @param {Object} query - API query
   * @param {string} query.companyId - Firma ID
   * @param {string} query.languageCode - Dil kodu (örn: "tr", "en")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getRoutesByCompany: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getRoutesByCompany}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Yunan adaları için en ucuz feribot rotalarını getir
   * @param {Object} query - API query
   * @param {string} query.languageCode - Dil kodu (örn: "TR", "EN")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getCheapestGreeceIslands: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestGreeceIslands}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Yunan adaları içeriğini getir
   * @param {Object} query - API query
   * @param {string} query.languageCode - Dil kodu (örn: "TR", "EN")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getGreeceIslandsContent: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getGreeceIslandsContent}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler Yunan adaları rotalarını getir
   * @param {Object} query - API query
   * @param {string} query.languageCode - Dil kodu (örn: "TR", "EN")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getPopularsGreeceIslands: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getPopularsGreeceIslands}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * En ucuz Kıbrıs feribot rotalarını getir
   * @param {Object} query - API query
   * @param {string} query.languageCode - Dil kodu (örn: "TR", "EN")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getCheapestCyprusFerries: (
    query = {},
    headers = {},
    hasErrorHandler = false,
  ) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCheapestCyprusFerries}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Kıbrıs içeriğini getir
   * @param {Object} query - API query
   * @param {string} query.languageCode - Dil kodu (örn: "TR", "EN")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getCyprusContent: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getCyprusContent}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler Kıbrıs rotalarını getir
   * @param {Object} query - API query
   * @param {string} query.languageCode - Dil kodu (örn: "TR", "EN")
   * @param {string} query.currencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getPopularsCyprus: (query = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.ferry.route.getPopularsCyprus}?${jsonToQuery(query)}`,
      headers,
      hasErrorHandler,
    }),
};
